import type { CompareHeroProps } from '@/components/pet/compare/CompareHero';
import type { CompareReviewsProps } from '@/components/pet/compare/CompareReviews';
import type { HowToCompareProps } from '@/components/pet/compare/HowToCompare';
import type { CompareTableProps } from '@/components/shared/CompareTable';
import { sharedConfig } from '@/pageConfig/shared';
import ogImage from '@/static/assets/pet/og.png';
import akc from '@/static/pet/compare/akc.png';
import ASPCA from '@/static/pet/compare/ASPCA.png';
import claimsImage from '@/static/pet/compare/claims.svg';
import customizationImage from '@/static/pet/compare/customization.svg';
import embrace from '@/static/pet/compare/embrace.png';
import fetch from '@/static/pet/compare/fetch.png';
import figo from '@/static/pet/compare/figo.png';
import hartville from '@/static/pet/compare/hartville.png';
import healthyPaws from '@/static/pet/compare/healthyPaws.png';
import manypets from '@/static/pet/compare/manypets.png';
import metLife from '@/static/pet/compare/metLife.png';
import nationwide from '@/static/pet/compare/nationwide.png';
import odie from '@/static/pet/compare/odie.png';
import petsBest from '@/static/pet/compare/petsBest.png';
import priceImage from '@/static/pet/compare/price.svg';
import prudent from '@/static/pet/compare/prudent.png';
import pumpkin from '@/static/pet/compare/pumpkin.png';
import spot from '@/static/pet/compare/spot.png';
import trupanion from '@/static/pet/compare/trupanion.png';
import kanguro from '@/static/pet/compare/Kanguro_desktop.png';
import felix from '@/static/pet/compare/Felix_desktop.png';
import wagmo from '@/static/pet/compare/Wagmo_desktop.png';

import { PRODUCT_PET } from '@/utils/products';
import type { BasePageConfig } from '../../../BasePageConfig';

interface Config extends BasePageConfig {
  hero: CompareHeroProps;
  compareTable: CompareTableProps;
  howToCompare: HowToCompareProps;
  compareReviews: Omit<CompareReviewsProps, 'show'>;
}

export const config: Config = {
  seo: {
    title: 'Compare Pet Insurance Plans by Lemonade',
    metaTitle: 'Compare Pet Insurance Plans by Lemonade',
    description: 'Compare the best pet insurance plans for your pet',
    ogImage: ogImage.src,
    uniqueCanonicalUrl: 'https://www.lemonade.com/pet/compare',
  },
  layout: { ...sharedConfig['en-US'].layout },
  hero: {
    title: 'Pet insurance comparison<br />made easy',
    subtitle: "Compare and find the best coverage for your pet (even if it's not ours)",
    mainCta: 'Get a quote',
  },
  compareTable: {
    title: 'How Lemonade pet insurance compares',
    subtitle:
      'Lemonade Pet was designed by vets and pet-obsessed people. See how our best-in-class coverage and\nopportunities to save stack up to the competition.',
    comparisonCriteria: [
      'Fully customizable coverages',
      'Comprehensive preventative plans',
      'Option to cover spaying / neutering',
      'Bundle discount',
      'Annual payment discount',
      'Multi-pet discount',
      'Instant claim handling',
      'Ability to submit claims through the app',
      'App store rating',
    ],
    lemonade: {
      product: PRODUCT_PET,
      ctaLabel: 'Get a quote',
      values: [true, true, true, true, true, true, true, true, '4.9'],
    },
    companies: [
      {
        companyName: 'Trupanion',
        companyLogo: trupanion,
        values: [false, false, false, false, false, false, true, false, 'N/A'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-trupanion/',
      },
      {
        companyName: 'Nationwide',
        companyLogo: nationwide,
        values: [true, true, true, true, false, true, false, false, '4.7'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-nationwide/',
      },
      {
        companyName: 'PetsBest',
        companyLogo: petsBest,
        values: [true, true, true, false, false, true, false, true, '4.6'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-pets-best/',
      },
      {
        companyName: 'Embrace',
        companyLogo: embrace,
        values: [false, true, true, false, false, true, false, true, '4.8'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-embrace/',
      },
      {
        companyName: 'MetLife',
        companyLogo: metLife,
        values: [true, true, true, false, false, false, false, true, '4.5'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-metlife/',
      },
      {
        companyName: 'Pumpkin',
        companyLogo: pumpkin,
        values: [true, true, true, false, false, true, true, false, 'N/A'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-pumpkin/',
      },
      {
        companyName: 'Fetch',
        companyLogo: fetch,
        values: [true, true, true, false, false, false, false, true, '4.8'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-fetch/',
      },
      {
        companyName: 'ASPCA',
        companyLogo: ASPCA,
        values: [true, true, true, false, false, true, false, true, '2.2'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-aspca/',
      },
      {
        companyName: 'Healthy Paws',
        companyLogo: healthyPaws,
        values: [false, false, false, false, false, false, false, true, '4.6'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-healthy-paws/',
      },
      {
        companyName: 'Figo',
        companyLogo: figo,
        values: [true, true, true, false, false, true, false, true, '4.7'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-figo/',
      },
      {
        companyName: 'Spot',
        companyLogo: spot,
        values: [true, true, true, false, false, true, false, true, '3.4'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-spot/',
      },
      {
        companyName: 'AKC',
        companyLogo: akc,
        values: [true, true, true, false, false, true, false, true, '4.8'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-akc/',
      },
      {
        companyName: 'Hartville',
        companyLogo: hartville,
        values: [true, true, true, false, false, true, false, true, 'N/A'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-hartville/',
      },
      {
        companyName: 'ManyPets',
        companyLogo: manypets,
        values: [false, true, false, false, false, false, false, true, '5'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-manypets/',
      },
      {
        companyName: 'Prudent',
        companyLogo: prudent,
        values: [false, true, true, false, false, true, false, false, 'N/A'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-prudent/',
      },
      {
        companyName: 'Odie',
        companyLogo: odie,
        values: [false, true, true, false, false, true, false, false, 'N/A'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-odie/',
      },
      {
        companyName: 'Kanguro',
        companyLogo: kanguro,
        values: [true, true, true, false, true, true, false, true, '4.5'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-kanguro/',
      },
      {
        companyName: 'Wagmo',
        companyLogo: wagmo,
        values: [false, true, false, false, false, true, false, true, '4.2'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-wagmo/',
      },
      {
        companyName: 'Felix',
        companyLogo: felix,
        values: [true, true, true, false, false, true, false, false, 'N/A'],
        reviewLink: 'https://www.lemonade.com/pet/compare/lemonade-vs-felix/',
      },
    ],
  },
  howToCompare: {
    title: 'How to choose the best pet insurance plan',
    subtitle: 'Ask yourself these questions to help you make the right decision for you and your pet',
    cards: [
      {
        content:
          'What do you and your pet need? Are you able to customize your coverage and premiums to meet those needs?',
        image: customizationImage,
        title: 'Customization',
      },
      {
        content: 'Can you file claims easily or the way you want? How long does it take for claims to be approved?',
        image: claimsImage,
        title: 'Claims',
      },
      {
        content:
          'How much do you feel comfortable paying for pet insurance? Do you prioritize lower premiums or higher payouts? ',
        image: priceImage,
        title: 'Price',
      },
    ],
  },
  compareReviews: {
    title: 'Pet parents love Lemonade',
    subtitle: `Customizable coverage, seamless claims, and compassionate customer support —it's no wonder why hundreds of thousands of pet parents choose <a href="https://www.lemonade.com/pet/">Lemonade pet insurance</a> to help cover their vet bills`,
  },
};
